import {useSocket} from "../SocketContext";
import React, {useEffect, useReducer, useState} from "react";
import {rpc_statsInit, ServerStats, ServerStatValue} from "./interfaces";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    TimeScale, Chart,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';
import { Maximize2, Minimize2 } from 'lucide-react';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    TimeScale
);

interface ChartView {
    startDate: string
    interval: number
    expanded?: string
    dateLocked?: boolean
}

interface ChartViewUpdate{
    startDate?: string
    interval?: number
    expanded?: string|null
}

export default function Stats(){

    const socket = useSocket()

    const [stats, setStats] = useState<ServerStats>()

    const [chart, updateChart] = useReducer((chart: ChartView, update: ChartViewUpdate) => {
        const newState = {...chart}

        if (update.startDate !== undefined){
            newState.startDate = update.startDate
            newState.dateLocked = true
        }
        if (update.interval !== undefined){
            newState.interval = update.interval
            let then = Date.parse(newState.startDate) + newState.interval * 24 * 60 * 60 * 1000
            if (then > Date.now()){
                newState.startDate = date(Date.now() - update.interval * 24 * 60 * 60 * 1000)
            }
        }
        if (update.expanded !== undefined){
            newState.expanded = update.expanded ?? undefined
        }

        return newState
    }, {startDate: date(), interval: 0})

    useEffect(() => {
        if (socket.isConnected){
            rpc_statsInit(socket, Date.parse(chart.startDate) / 1000, chart.interval, chart.expanded ? chart.interval <= 7 : chart.interval <= 2).then(s => {
                if (s){
                    console.log('Loaded stats data', s);
                    setStats(s)
                } else {
                    console.error('Failed to load stats data');
                }
            })
        }
    }, [socket, chart]);

    Chart.defaults.color = '#666';

    if (!stats){
        return <div className="size-full flex flex-col xl:flex-row p-4 relative">
            <div className="w-full flex flex-col items-center justify-center">
                <div className="text-xl font-bold">Loading...</div>
            </div>
        </div>
    }

    const chartConfigs = [
        { title: "Player Count", values: stats.players },
        { title: "Server FPS", values: stats.fps },
        { title: "Server Memory (MB)", values: stats.memory },
        { title: "Entity Count", values: stats.entities },
    ];

    return (
        <div className="size-full flex flex-col xl:flex-row p-4 relative">

            <div className="flex flex-col w-full overflow-y-auto">
                <div className="mb-4 flex justify-between items-center">
                    <div className="space-y-2">
                        <h1 className="text-2xl font-bold text-white">Server Statistics</h1>
                        <div className="flex flex-row gap-4 items-center">
                            <div className="flex flex-row items-center gap-2">
                                <label htmlFor="stats-interval-start">Interval</label>
                                <div className="flex flex-row gap-2 bg-neutral-600 p-1 rounded-md">
                                    <ChannelFilterButton text="CURRENT WIPE" interval={0}/>
                                    <ChannelFilterButton text="1D" interval={1}/>
                                    <ChannelFilterButton text="1W" interval={7}/>
                                    <ChannelFilterButton text="1M" interval={30}/>
                                </div>

                            </div>
                            {chart.interval > 0 && <div className="flex flex-row items-center gap-2">
                                <label htmlFor="stats-interval-start">Start</label>
                                <input id="stats-interval-start" type="date" value={chart.startDate}
                                       onChange={(e) => updateChart({startDate: e.target.value})}
                                       className="bg-neutral-600 p-1 rounded-md text-sm outline-none border-none [color-scheme:dark]"
                                />
                            </div>}
                        </div>
                    </div>
                    {chart.expanded && (
                        <button
                            onClick={() => updateChart({expanded: null})}
                            className="flex items-center gap-2 bg-zinc-800 hover:bg-zinc-700 px-4 py-2 rounded-md transition-colors"
                        >
                            <Minimize2 size={18} />
                            Back to Grid
                        </button>
                    )}
                </div>

                {chart.expanded ? (
                    <div className="size-full bg-neutral-900 p-4 rounded-lg border border-neutral-500">
                        {chartConfigs.filter(c => c.title === chart.expanded).map(c => (
                            <StatsChart
                                key={c.title}
                                title={c.title}
                                values={c.values}
                                isExpanded={true}
                                onToggleExpand={() => updateChart({expanded: null})}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {chartConfigs.map(c => (
                            <div key={c.title} className="h-96 bg-neutral-900 p-4 rounded-lg border border-neutral-500 relative group">
                                <StatsChart
                                    title={c.title}
                                    values={c.values}
                                    onToggleExpand={() => updateChart({expanded: c.title})}
                                />
                            </div>
                        ))}
                    </div>
                )}
                <div className="flex-grow"></div>
                <p className="text-neutral-500 mt-2 text-center">All statistics are delayed by a few hours for technical reasons</p>
            </div>
        </div>
    )

    function ChannelFilterButton(props: {text: string, interval: number}){
        return <button onClick={() => updateChart({ interval: props.interval })}
                       className={`rounded-md px-2 ${chart.interval === props.interval ? "bg-primary" : "bg-neutral-600"}`} >
            {props.text}
        </button>
    }

    function date(unix?: number) {
        return new Date(unix ?? Date.now()).toISOString().split('T')[0]
    }
}

export enum StatsType{
    PLAYER_COUNT = "Player Count",
    SERVER_FPS = "Server FPS",
    SERVER_MEMORY = "Server Memory",
    ENTITY_COUNT = "Entity Count",
}

export function StatsWidget(props: {stats?: ServerStatValue[], type: StatsType}) {

    const socket = useSocket()
    const [stats, setStats] = useState<ServerStatValue[]>([])

    useEffect(() => {
        if (props.stats){
            setStats(props.stats)
        }
        else{
            rpc_statsInit(socket, Date.now(), 0).then(s => {
                if (!s){
                    console.error('Failed to load stats data');
                    return
                }
                if (props.type === StatsType.SERVER_FPS){
                    setStats(s.fps)
                }
                else if (props.type === StatsType.SERVER_MEMORY){
                    setStats(s.memory)
                }
                else if (props.type === StatsType.ENTITY_COUNT){
                    setStats(s.entities)
                }
                else if (props.type === StatsType.PLAYER_COUNT){
                    setStats(s.players)
                }
            })
        }
    }, [props.stats, props.type, socket]);

    return <div className="h-full relative group">
        <StatsChart title={props.type} values={stats} />
    </div>
}

export function StatsChart(props: {values: ServerStatValue[], title: string, isExpanded?: boolean, onToggleExpand?: () => void}) {
    const data = {
        datasets: [
            {
                label: '1st %',
                data: props.values.map(value => ({
                    x: value.timestamp * 1000, // Convert to milliseconds
                    y: value.p01
                })),
                borderColor: 'rgb(239, 68, 68)', // red-500
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                tension: 0.1,
            },
            // {
            //     label: '5th %',
            //     data: props.values.map(value => ({
            //         x: value.timestamp * 1000, // Convert to milliseconds
            //         y: value.p05
            //     })),
            //     borderColor: 'rgb(239, 68, 68)', // red-500
            //     backgroundColor: 'rgba(239, 68, 68, 0.1)',
            //     tension: 0.1,
            //     hidden: true
            // },
            {
                label: 'Median',
                data: props.values.map(value => ({
                    x: value.timestamp * 1000,
                    y: value.p50
                })),
                borderColor: 'rgb(59, 130, 246)', // blue-500
                backgroundColor: 'rgba(59, 130, 246, 0.1)',
                tension: 0.1,
            },
            // {
            //     label: '95th %',
            //     data: props.values.map(value => ({
            //         x: value.timestamp * 1000,
            //         y: value.p95
            //     })),
            //     borderColor: 'rgb(34, 197, 94)', // green-500
            //     backgroundColor: 'rgba(34, 197, 94, 0.1)',
            //     tension: 0.1,
            //     hidden: true
            // },
            {
                label: '99th %',
                data: props.values.map(value => ({
                    x: value.timestamp * 1000,
                    y: value.p99
                })),
                borderColor: 'rgb(34, 197, 94)', // green-500
                backgroundColor: 'rgba(34, 197, 94, 0.1)',
                tension: 0.1,
            },
        ],
    };

    const options = {
        defaultFontColor: '#fff',
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top' as const,
            },
            title: {
                display: true,
                text: props.title,
            },
        },
        scales: {
            x: {
                type: 'time' as const,
                time: {
                    displayFormats: {
                        millisecond: 'HH:mm:ss.SSS',
                        second: 'HH:mm:ss',
                        minute: 'HH:mm',
                        hour: 'HH:mm',
                        day: 'MMM dd',
                        week: 'MMM dd',
                        month: 'MMM yyyy',
                        quarter: 'MMM yyyy',
                        year: 'yyyy',
                    },
                }
            },
            y: {
                beginAtZero: false
            },
        },
    };

    return (
        <div className="size-full relative">
            {props.onToggleExpand && (
                <button
                    onClick={props.onToggleExpand}
                    className="absolute top-0 right-0 z-10 p-2 text-neutral-500 hover:text-white transition-colors"
                    title={props.isExpanded ? "Minimize" : "Maximize"}
                >
                    {props.isExpanded ? <Minimize2 size={20} /> : <Maximize2 size={20} />}
                </button>
            )}
            <Line data={data} options={options} />
        </div>
    );
}